import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import TranslationProvider from '../../providers/TranslationProvider';
import containerQueryProps from '../../props/containerQueryProps';

import './Auth.scss';
import InputCode from '../Account/UpdatePhone/InputCode';
import Button from '../Button';
import parsePhone from '../../utils/parsePhone';
import { AuthFlowType } from '../../containers/AuthContainer/authSteps';
import {
  AUTH_ACTIONS,
  AUTH_FLOWS,
  AUTH_METHODS,
  AUTH_STEPS,
  buildAuthDdActionName,
  trackAuthDdAction,
} from '../../utils/authDatadog';

const VerifyCodeView = ({
  onSubmit,
  onResendCode,
  onHelpClick,
  phone,
  containerQuery,
  maskedEmail,
  errorMessage,
  authFlowType,
  processing,
}) => {
  const { sm } = containerQuery;
  let resolvedFlow = AUTH_FLOWS.SIGN_IN;
  if (!maskedEmail && authFlowType === AuthFlowType.VERIFY_PHONE_TO_SIGN_UP) {
    resolvedFlow = AUTH_FLOWS.SIGN_UP;
  }
  const resolvedMethod = maskedEmail ? AUTH_METHODS.EMAIL_CODE : AUTH_METHODS.PHONE_CODE;
  const resolvedStep = maskedEmail ? AUTH_STEPS.VERIFY_EMAIL_CODE : AUTH_STEPS.VERIFY_PHONE_CODE;
  const helpActionName = buildAuthDdActionName({
    flow: resolvedFlow,
    method: resolvedMethod,
    step: resolvedStep,
    action: AUTH_ACTIONS.SWITCH_METHOD,
  });

  const renderCopy = () => {
    if (maskedEmail) {
      return (
        <div>
          <TranslationProvider
            translationKey="onboarding.code_title_email"
          >
            <div className="AuthView__Text--bold AuthView__Help--Title">
              Check your email!
            </div>
          </TranslationProvider>
          <div
            className="AuthView__Text--light AuthView__Help--Copy"
          >

            <TranslationProvider
              translationKey="onboarding.code_copy_no_password"
            >
              <div>
                No need for a password.
              </div>
            </TranslationProvider>
            <TranslationProvider
              translationKey="onboarding.code_copy_one"
              translationValues={{ destination: `<span data-dd-privacy="mask" class="AuthView__Text--email">${maskedEmail}</span>` }}
            >
              <div>
                We sent a 6-digit confirmation code to
                {' '}
                <span data-dd-privacy="mask" className="AuthView__Text--email">
                  {maskedEmail}
                </span>
                .
              </div>
            </TranslationProvider>
          </div>
        </div>
      );
    }
    const phoneObj = parsePhone(phone);

    return (
      <div>
        <TranslationProvider
          translationKey="onboarding.code_title"
        >
          <div className="AuthView__Text--bold VerifyCodeViewTitle">
            Check your mobile phone!
          </div>
        </TranslationProvider>
        {authFlowType !== AuthFlowType.VERIFY_PHONE_TO_SIGN_UP && (
        <TranslationProvider translationKey="onboarding.code_copy_no_password">
          <div>
            No need for a password.
          </div>
        </TranslationProvider>
        )}
        <div
          className="AuthView__Text--light VerifyCodeViewTextDesc"
        >
          <TranslationProvider
            translationKey="onboarding.code_copy_one"
            translationValues={{
              destination: `<span data-dd-privacy="mask" class="AuthView__Text--phone">+${phoneObj.countryCode} ${phoneObj.nationalFormat}</span>`,
            }}
          >
            <span>
              We sent a 6-digit confirmation code to
              {' '}
              <span data-dd-privacy="mask" className="AuthView__Text--phone">
                {`+${phoneObj.countryCode} ${phoneObj.nationalFormat}`}
              </span>
              .
            </span>
          </TranslationProvider>
        </div>
      </div>
    );
  };

  return (
    <div
      className={cx('AuthView', {
        'AuthView--sm': sm,
      })}
    >
      {renderCopy()}
      <InputCode
        onComplete={onSubmit}
        processing={processing}
        error={errorMessage}
        completeDatadogActionName={buildAuthDdActionName({
          flow: resolvedFlow,
          method: resolvedMethod,
          step: resolvedStep,
          action: AUTH_ACTIONS.SUBMIT,
        })}
        onResend={onResendCode}
        resendDatadogActionName={buildAuthDdActionName({
          flow: resolvedFlow,
          method: resolvedMethod,
          step: resolvedStep,
          action: AUTH_ACTIONS.RESEND_CODE,
        })}
      />
      {!processing && (
      <div
        className={cx('AuthView__Footer VerifyCodeViewFooter', {
          'AuthView__Footer--sm': sm,
        })}
      >
        <TranslationProvider
          translationKey="onboarding.need_help"
        >
          <Button
            onClick={() => {
              trackAuthDdAction(helpActionName, { source: 'auth' });
              onHelpClick();
            }}
            type="button"
            variant="link"
            className="HelpButton"
            id="help-button"
            data-dd-action-name={helpActionName}
          >
            Need Help?
          </Button>
        </TranslationProvider>
      </div>
      )}
    </div>
  );
};

VerifyCodeView.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onResendCode: PropTypes.func.isRequired,
  onHelpClick: PropTypes.func.isRequired,
  phone: PropTypes.string.isRequired,
  containerQuery: containerQueryProps,
  maskedEmail: PropTypes.string,
  errorMessage: PropTypes.string,
  authFlowType: PropTypes.string,
  processing: PropTypes.bool,
};

VerifyCodeView.defaultProps = {
  containerQuery: {},
  maskedEmail: null,
  errorMessage: null,
  processing: false,
  authFlowType: AuthFlowType.DEFAULT,
};

VerifyCodeView.displayName = 'VerifyCodeView';

export default VerifyCodeView;
